/**
 * Footnotes Section Component
 *
 * Displays footnotes for the selected deckline or all page footnotes.
 * Provides "View All" toggle to show all footnotes on the page.
 * Read-only display component.
 *
 * @returns Footnotes section component
 */

import './Footnotes.scss';

import { useDecklinesContext } from '@context/DecklinesContext';
import { useFootnotesContext } from '@context/FootnotesContext.tsx';
import { Paper } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Footnote } from '@services/api/deckManagement/graphql/schema/API';
import { isNil } from 'lodash';
import { CSSProperties, Ref, useCallback, useEffect, useMemo, useState } from 'react';
import { FootnotesSectionProps } from 'types/Footnotes.types';

const FootnotesSection = ({ ref, ...props }: FootnotesSectionProps & { ref?: Ref<HTMLDivElement> }) => {
  const { containerWidth } = props;
  const { selectedDeckline } = useDecklinesContext();
  const { footnotes } = useFootnotesContext();
  const [viewAllToggle, setViewAllToggle] = useState<boolean>(true);

  /**
   * When selectedDeckline changes, this disables `View All` mode so that
   * only deckline-specific footnotes will be displayed
   */
  useEffect(() => {
    if (!isNil(selectedDeckline)) {
      setViewAllToggle(false);
    }
  }, [selectedDeckline]);

  // Handles populating CSS variables which are optional.
  const cssStyle = useMemo(() => {
    const cssStyleBuffer: Record<string, string | number> = {};
    if (containerWidth) {
      cssStyleBuffer['--footnotes-container-width'] = `${containerWidth}px`;
    }

    return cssStyleBuffer as CSSProperties;
  }, [containerWidth]);

  /**
   * Generate footnotes content for display
   * Filters footnotes based on selected deckline or view all toggle
   *
   * @returns TextField with formatted footnotes or null if no footnotes to display
   */
  const generateFootnotesContent = useCallback(() => {
    const label = viewAllToggle ? 'All' : selectedDeckline?.metadata.actual_kpi_name || '';
    const filteredFootnotes = viewAllToggle
      ? footnotes
      : footnotes.filter((footnote: Footnote) =>
          (selectedDeckline?.footnotes || []).includes(parseInt(footnote.footnote_no!, 10)),
        );
    let footnotesContent = '';
    filteredFootnotes.forEach((footnote: Footnote) => {
      footnotesContent += `${footnote.footnote_no} ${footnote.footnote_description}\n`;
    });
    footnotesContent = footnotesContent.substring(0, footnotesContent.length - 1);
    // return null if no footnotes to display
    if ((!viewAllToggle && !selectedDeckline) || footnotesContent.trim().length === 0) {
      return null;
    }

    return (
      <TextField
        fullWidth
        multiline
        minRows={1}
        maxRows={4}
        value={footnotesContent}
        disabled
        label={label}
        margin='normal'
        className='footnotes-section-content'
        autoComplete='off'
      />
    );
  }, [viewAllToggle, footnotes, selectedDeckline]);

  return (
    <Paper className='footnotes-section' style={cssStyle} elevation={6} ref={ref}>
      <Box className='footnotes-section-header'>
        <Typography variant='h6'>
          {viewAllToggle && 'All'} Footnotes {footnotes.length === 0 && '(None)'}
        </Typography>
        {footnotes.length > 0 && (
          <Button
            size='small'
            onClick={() => {
              setViewAllToggle((prev) => !prev);
            }}
          >
            {viewAllToggle ? 'Collapse' : 'View All'}
          </Button>
        )}
      </Box>
      {generateFootnotesContent()}
    </Paper>
  );
};

export default FootnotesSection;
